interface ErrorEvent {
  error: Error;
  componentStack?: string;
  eventId: string;
  tags?: Record<string, string>;
}

interface AnalyticsEvent {
  name: string;
  properties?: Record<string, any>;
  timestamp: number;
}

class Monitoring {
  private static instance: Monitoring;
  private errorQueue: ErrorEvent[] = [];
  private analyticsQueue: AnalyticsEvent[] = [];
  private flushInterval: number = 5000; // 5 seconds

  private constructor() {
    this.startFlushTimer();
    this.setupErrorListeners();
  }

  static getInstance(): Monitoring {
    if (!Monitoring.instance) {
      Monitoring.instance = new Monitoring();
    }
    return Monitoring.instance;
  }

  private setupErrorListeners() {
    window.addEventListener('error', (event) => {
      this.captureError(event.error);
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.captureError(event.reason);
    });
  }

  private startFlushTimer() {
    setInterval(() => this.flush(), this.flushInterval);
  }

  captureError(error: Error, componentStack?: string, tags?: Record<string, string>) {
    const errorEvent: ErrorEvent = {
      error,
      componentStack,
      eventId: Math.random().toString(36).substring(2, 15),
      tags
    };

    this.errorQueue.push(errorEvent);
    console.error('[Error Tracked]:', errorEvent);
  }

  trackEvent(name: string, properties?: Record<string, any>) {
    const event: AnalyticsEvent = {
      name,
      properties,
      timestamp: Date.now()
    };

    this.analyticsQueue.push(event);
    console.log('[Event Tracked]:', event);
  }

  private async flush() {
    if (this.errorQueue.length === 0 && this.analyticsQueue.length === 0) return;

    try {
      // In a real implementation, this would send to your error tracking service
      if (this.errorQueue.length > 0) {
        console.log('Flushing errors:', this.errorQueue);
        this.errorQueue = [];
      }

      // In a real implementation, this would send to your analytics service
      if (this.analyticsQueue.length > 0) {
        console.log('Flushing analytics:', this.analyticsQueue);
        this.analyticsQueue = [];
      }
    } catch (error) {
      console.error('Error flushing monitoring data:', error);
    }
  }
}

export const monitoring = Monitoring.getInstance();

// Usage example:
// monitoring.captureError(new Error('Something went wrong'));
// monitoring.trackEvent('button_click', { buttonId: 'submit' });
